import React, { useState } from 'react';
import { apiCall } from '../api';
import { useMountEffect } from '../hooks/useMountEffect';
import './AdminPages.css';

const Analytics = () => {
  const [stats, setStats] = useState({
    totalWorkOrders: 0,
    completedOrders: 0,
    failedOrders: 0,
    pendingOrders: 0,
    processingOrders: 0,
    totalFiles: 0,
    avgProcessingTime: 0,
    successRate: 0,
    fillRate: 0,
    changeRate: 0,
    totalExtractions: 0,
    changedExtractions: 0,
    fileTypes: {},
    fileTypeCount: 0,
    pipelines: {}
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');

  const fetchStats = async () => {
    try {
      const data = await apiCall('/api/analytics/stats');
      setStats(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  useMountEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  });

  const formatTime = (seconds) => {
    if (!seconds || seconds <= 0) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh', gap: '1rem' }}>
          <div style={{ width: 44, height: 44, border: '4px solid #e5e7eb', borderTop: '4px solid #667eea', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <p style={{ color: '#6b7280' }}>Loading analytics...</p>
        </div>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page">
        <div style={{ marginBottom: '2.5rem' }}>
          <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: 700, color: '#1f2937', marginBottom: '0.5rem' }}>
            📊 Analytics Dashboard
          </h1>
        </div>
        <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', padding: '1.5rem', borderRadius: '12px', color: '#991b1b', textAlign: 'center' }}>
          <p style={{ margin: '0 0 1rem 0', fontSize: '1rem' }}>{error}</p>
          <button onClick={fetchStats} style={{ padding: '0.5rem 1.5rem', background: '#667eea', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>Retry</button>
        </div>
      </div>
    );
  }

  const pipelineList = [
    { key: 'all', name: 'All Pipelines', color: '#667eea' },
    { key: 'glib', name: 'GLIB (Main)', color: '#667eea' },
    { key: 'v1', name: 'XOrbit / VJA', color: '#8b5cf6' },
    { key: 'cocb', name: 'COCB', color: '#f59e0b' },
    { key: 'gfs', name: 'GFS', color: '#10b981' }
  ];

  // Get active data based on tab
  const getActiveStats = () => {
    if (activeTab === 'all') {
      return {
        totalWorkOrders: stats.totalWorkOrders,
        completedOrders: stats.completedOrders,
        failedOrders: stats.failedOrders,
        pendingOrders: stats.pendingOrders,
        processingOrders: stats.processingOrders,
        successRate: stats.successRate,
        avgProcessingTime: stats.avgProcessingTime
      };
    }
    const p = stats.pipelines?.[activeTab];
    if (!p) return { totalWorkOrders: 0, completedOrders: 0, failedOrders: 0, pendingOrders: 0, processingOrders: 0, successRate: 0, avgProcessingTime: 0 };
    return p;
  };

  const active = getActiveStats();
  const activeColor = pipelineList.find(p => p.key === activeTab)?.color || '#667eea';

  const statuses = [
    { label: 'Completed', value: active.completedOrders, color: '#10b981', bgColor: '#d1fae5' },
    { label: 'Processing', value: active.processingOrders, color: '#f59e0b', bgColor: '#fef3c7' },
    { label: 'Pending', value: active.pendingOrders, color: '#3b82f6', bgColor: '#dbeafe' },
    { label: 'Failed', value: active.failedOrders, color: '#ef4444', bgColor: '#fee2e2' }
  ];

  const fileTypesArray = Object.entries(stats.fileTypes || {}).slice(0, 5);
  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  return (
    <div className="admin-page">
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: 700, color: '#1f2937', marginBottom: '0.5rem' }}>
          📊 Analytics Dashboard
        </h1>
        <p style={{ margin: 0, color: '#6b7280', fontSize: '0.95rem' }}>Monitor extraction performance across all pipelines</p>
      </div>

      {/* Pipeline Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem', flexWrap: 'wrap', background: '#f3f4f6', padding: '0.5rem', borderRadius: '10px' }}>
        {pipelineList.map(pl => (
          <button
            key={pl.key}
            onClick={() => setActiveTab(pl.key)}
            style={{
              padding: '0.6rem 1.2rem',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: 600,
              transition: 'all 0.2s',
              background: activeTab === pl.key ? pl.color : 'transparent',
              color: activeTab === pl.key ? 'white' : '#4b5563',
              boxShadow: activeTab === pl.key ? `0 2px 8px ${pl.color}33` : 'none'
            }}
          >
            {pl.name}
          </button>
        ))}
      </div>

      {/* Top KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.5rem', marginBottom: '2.5rem' }}>
        <div style={{ background: `linear-gradient(135deg, ${activeColor} 0%, ${activeColor}99 100%)`, padding: '1.5rem', borderRadius: '12px', color: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Work Orders</div>
          <div style={{ fontSize: '2.5rem', fontWeight: 700 }}>{active.totalWorkOrders}</div>
          <div style={{ fontSize: '0.8rem', opacity: 0.8, marginTop: '0.5rem' }}>{activeTab === 'all' ? 'All pipelines' : pipelineList.find(p => p.key === activeTab)?.name}</div>
        </div>

        <div style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', padding: '1.5rem', borderRadius: '12px', color: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Success Rate</div>
          <div style={{ fontSize: '2.5rem', fontWeight: 700 }}>{(active.successRate || 0).toFixed(1)}%</div>
          <div style={{ fontSize: '0.8rem', opacity: 0.8, marginTop: '0.5rem' }}>{active.completedOrders} completed</div>
        </div>

        <div style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', padding: '1.5rem', borderRadius: '12px', color: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Fill Rate</div>
          <div style={{ fontSize: '2.5rem', fontWeight: 700 }}>{activeTab === 'all' ? (stats.fillRate || 0).toFixed(1) : '-'}%</div>
          <div style={{ fontSize: '0.8rem', opacity: 0.8, marginTop: '0.5rem' }}>Data extraction</div>
        </div>

        <div style={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', padding: '1.5rem', borderRadius: '12px', color: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Avg Processing Time</div>
          <div style={{ fontSize: '2rem', fontWeight: 700 }}>
            {formatTime(active.avgProcessingTime)}
          </div>
          <div style={{ fontSize: '0.8rem', opacity: 0.8, marginTop: '0.5rem' }}>per work order</div>
        </div>
      </div>

      {/* Status Distribution & File Types */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2.5rem' }}>
        {/* Work Order Status Distribution */}
        <div style={{ background: 'white', padding: '2rem', borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.1rem', fontWeight: 600, color: '#1f2937' }}>📋 Work Order Status</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {statuses.map(status => (
              <div key={status.label}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: status.color }} />
                    <span style={{ fontSize: '0.9rem', fontWeight: 500, color: '#1f2937' }}>{status.label}</span>
                  </div>
                  <span style={{ fontSize: '0.95rem', fontWeight: 700, color: status.color }}>{status.value}</span>
                </div>
                <div style={{ height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                  <div
                    style={{
                      height: '100%',
                      background: status.color,
                      width: `${active.totalWorkOrders > 0 ? (status.value / active.totalWorkOrders) * 100 : 0}%`,
                      transition: 'width 0.3s ease'
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* File Types */}
        <div style={{ background: 'white', padding: '2rem', borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.1rem', fontWeight: 600, color: '#1f2937' }}>📁 File Types Distribution</h3>
          {fileTypesArray.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              {fileTypesArray.map(([type, count], idx) => {
                const total = Object.values(stats.fileTypes).reduce((a, b) => a + b, 0);
                const percentage = (count / total) * 100;
                return (
                  <div key={type}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: colors[idx] }} />
                        <span style={{ fontSize: '0.9rem', fontWeight: 500, color: '#1f2937' }}>{type}</span>
                      </div>
                      <span style={{ fontSize: '0.95rem', fontWeight: 700, color: colors[idx] }}>{count} files ({percentage.toFixed(1)}%)</span>
                    </div>
                    <div style={{ height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                      <div
                        style={{
                          height: '100%',
                          background: colors[idx],
                          width: `${percentage}%`,
                          transition: 'width 0.3s ease'
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ textAlign: 'center', color: '#999', padding: '2rem' }}>No file data available</div>
          )}
        </div>
      </div>

      {/* Pipeline Comparison (only shown on "All Pipelines" tab) */}
      {activeTab === 'all' && stats.pipelines && Object.keys(stats.pipelines).length > 0 && (
        <div style={{ background: 'white', padding: '2rem', borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.1rem', fontWeight: 600, color: '#1f2937' }}>🔀 Pipeline Comparison</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.8rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase' }}>Pipeline</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontSize: '0.8rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase' }}>Total</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontSize: '0.8rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase' }}>Completed</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontSize: '0.8rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase' }}>Failed</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontSize: '0.8rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase' }}>Success Rate</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontSize: '0.8rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase' }}>Avg Time</th>
                </tr>
              </thead>
              <tbody>
                {pipelineList.filter(pl => pl.key !== 'all').map(pl => {
                  const p = stats.pipelines[pl.key];
                  if (!p) return null;
                  return (
                    <tr key={pl.key} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ width: 10, height: 10, borderRadius: '50%', background: pl.color, display: 'inline-block' }} />
                          <span style={{ fontWeight: 600, color: '#1f2937' }}>{pl.name}</span>
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem 1rem', textAlign: 'center', fontWeight: 700 }}>{p.totalWorkOrders}</td>
                      <td style={{ padding: '0.75rem 1rem', textAlign: 'center', color: '#10b981', fontWeight: 600 }}>{p.completedOrders}</td>
                      <td style={{ padding: '0.75rem 1rem', textAlign: 'center', color: '#ef4444', fontWeight: 600 }}>{p.failedOrders}</td>
                      <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                        <span style={{ background: `${pl.color}15`, color: pl.color, padding: '3px 10px', borderRadius: 6, fontWeight: 700, fontSize: '0.85rem' }}>{(p.successRate || 0).toFixed(1)}%</span>
                      </td>
                      <td style={{ padding: '0.75rem 1rem', textAlign: 'center', color: '#6b7280', fontWeight: 500 }}>{formatTime(p.avgProcessingTime)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Extraction Metrics */}
      {activeTab === 'all' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginTop: '2rem' }}>
          {/* Key Value Pairs */}
          <div style={{ background: 'white', padding: '2rem', borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.1rem', fontWeight: 600, color: '#1f2937' }}>🔑 Key-Value Pairs</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', alignItems: 'center' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Extracted</div>
                <div style={{ fontSize: '2.5rem', fontWeight: 700, color: '#3b82f6' }}>{stats.totalExtractions}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Changed</div>
                <div style={{ fontSize: '2.5rem', fontWeight: 700, color: '#f59e0b' }}>{stats.changedExtractions || 0}</div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.5rem' }}>({stats.totalExtractions > 0 ? (((stats.changedExtractions || 0) / stats.totalExtractions) * 100).toFixed(1) : 0}%)</div>
              </div>
            </div>
          </div>

          {/* Change Rate */}
          <div style={{ background: 'white', padding: '2rem', borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.1rem', fontWeight: 600, color: '#1f2937' }}>📈 Change Rate</h3>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Overall Change Rate</div>
              <div style={{ height: '150px', background: '#f9fafb', borderRadius: '8px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '2rem' }}>
                <div style={{ width: '80px', height: `${Math.max(stats.changeRate || 0, 15)}%`, background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)', borderRadius: '6px 6px 0 0', position: 'relative', transition: 'height 0.3s ease' }}>
                  <div style={{ position: 'absolute', top: '-28px', left: '50%', transform: 'translateX(-50%)', fontSize: '1.2rem', fontWeight: 700, color: '#667eea' }}>{(stats.changeRate || 0).toFixed(1)}%</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;
